import React from 'react';
import PropTypes from 'prop-types';
import {
  reduxForm, getFormValues, change, Field,
} from 'redux-form';
import { t } from 'i18next';
import { connect } from 'react-redux';
import {
  FormContainer,
  Icon,
  ExpandButton,
  Container,
  PageTitle,
  CheckboxContainer,
  RadioButtonsContainer,
  NumbersInputInstructions,
  NumbersInputInstructionsTitle,
  NumbersInputInstructionsInfo,
  NumbersInputWrapper,
  InputsContainer,
  SubmitButtonContainer,
  ZipCodeInputContainer,
  NoResultsDisplay,
  NoResultsText,
  ErrorIcon,
} from './css';
import ArrowDown from '../../assets/icons/ArrowDown.svg';
import {
  strategies,
  formName,
  initialValues,
  validnumberregex,
  MAX_TRACKING_NUMBERS,
} from './constants';
import { ShipmentHistoryStates } from '../ViewUserDashboard/constants';
import {
  isGroupOfShipmentNumbersValid, isZipPostalCodeValid, validateForm,
} from './validation';
import {
  apiGetShipmentSummaryByShipmentNumbers,
  apiGetOrderTrackingData,
} from './api';
import { apiGetOrderById } from '../../services/orders';
import {
  getShipmentTrackingDetailed,
} from '../../services/shipments';
import ShipmentDetails from '../ReactComponent/ShipmentDetails';
import { getDeliveryDate } from '../../utils/dateTime';
import Checkbox from '../../lib/ui/form_elements/Checkbox';
import RadioButton from '../../lib/ui/form_elements/RadioButton';
import TextArea from '../../lib/ui/form_elements/TextArea';
import TextInput from '../../lib/ui/form_elements/TextInput';
import PrimaryButton from '../../lib/ui/buttons/PrimaryButton';
import { isRequired } from '../../utils/validator';
import Tooltip from '../../lib/ui/form_elements/Tooltip';
import Table from './Table';
import { TableContainer } from './Table/css';
import { DIVISION_VALUE_MATCHER } from './utils';
import QuestionsBlock from '../ReactComponent/QuestionsBlock';

const isExceptionTouchPoint = (status, statusCode) => (/^Exception - Call Customer Service$/i.test(status)
  || /^Exception - Appeler le service à la clientèle/i.test(status)
  || /^Exception - OSD/i.test(status)
  || /^Livré - OSD/i.test(status))
  && statusCode === 'COMPLETE';

class TrackShipmentForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStrategy: strategies.shipment,
      data: [],
      noResults: false,
      notFound: false,
      shipmentsFromHomePage: false,
    };
  }

  componentDidMount() {
    // if the user inputs numbers into the tracking bar of the homepage,
    // update the formData
    // once updated (componentDidUpdate), submit the form
    const urlParams = new URLSearchParams(window.location.search);
    const shipmentsFromHomePage = urlParams.get('homeProbillNumbers');

    if (shipmentsFromHomePage) {
      this.setState({ shipmentsFromHomePage: true });
      this.props.dispatch(change(formName, 'shipmentNumbers', shipmentsFromHomePage));
    }

    if (this.props.isAuthenticated) {
      this.props.dispatch(change(formName, 'division', this.props.division));
    }
  }

  componentDidUpdate() {
    // only do this the first time the page loads
    if (this.state.shipmentsFromHomePage) {
      this.handleFormSubmit();
    }
  }

  filterDetailsAfterDelivered = (detailsData) => {
    if (!detailsData || detailsData.length === 0) {
      return detailsData;
    }

    // Find the index where "Delivered" status appears
    // Also track the latest delivered timestamp to handle multiple "delivered" statuses
    let deliveredIndex = -1;
    let latestDeliveredTime = null;

    for (let i = 0; i < detailsData.length; i++) {
      const item = detailsData[i];
      const status = item.status?.toLowerCase() || '';

      if (status.includes('delivered') || status.includes('livré') || status.includes('order canceled') || status.includes('commande annulée')) {
        // Get the timestamp for this delivered status
        const currentTime = item.date;

        // If this is the first "delivered" found, or if it has a later timestamp
        if (deliveredIndex === -1
          || (currentTime && (!latestDeliveredTime || new Date(currentTime) >= new Date(latestDeliveredTime)))) {
          deliveredIndex = i;
          latestDeliveredTime = currentTime;
        }
      }
    }

    // If no "Delivered" status found, return original data
    if (deliveredIndex === -1) {
      return detailsData;
    }

    // Return only items up to and including the "Delivered" status
    return detailsData.slice(0, deliveredIndex + 1);
  };

  getDivision = () => {
    const { formData: { LTLOrCommerce } } = this.props;
    return DIVISION_VALUE_MATCHER[LTLOrCommerce];
  };

  getShipmentNumbers = () => {
    const { formData } = this.props;
    const shipmentNumbers = formData && formData.shipmentNumbers ? formData.shipmentNumbers : '';

    return shipmentNumbers
      .split(validnumberregex)
      .map((num) => (num ? num.trim() : undefined))
      .reduce((accum, curr) => (curr ? accum.concat(curr) : accum), []);
  };

  renderDetailsDataError = (index) => {
    const { data } = this.state;
    const dataRow = data[index];

    if (!dataRow) {
      return;
    }

    dataRow.separateRow = <ShipmentDetails error={t('ShipmentDetailsError')} />;
    this.setState({ data });
  };

  renderDetailsData = (index, detailsData) => {
    const { data } = this.state;
    const { isAuthenticated } = this.props;
    const division = this.getDivision();
    const dataRow = data[index];

    if (!dataRow) {
      return;
    }

    const { basicData } = dataRow;
    const historyData = detailsData.filter((history) => history.status !== ShipmentHistoryStates.DoNotShow);

    dataRow.detailsData = historyData;
    dataRow.separateRow = (
      <ShipmentDetails
        key={index}
        mapid={`google-map-${index}`}
        basicData={basicData}
        detailsData={historyData}
        isAuthenticated={isAuthenticated}
        division={division}
      />
    );

    this.setState({ data });
  };

  fetchAndRenderDetailsData = (index) => {
    const { data } = this.state;
    const dataRow = data[index];

    if (!dataRow) {
      return null;
    }

    const { detailsData } = dataRow;

    if (detailsData) {
      this.renderDetailsData(index, detailsData);
      return null;
    }

    if (detailsData) {
      this.renderDetailsData(index, detailsData);
    } else {
      this.renderDetailsDataError(index);
    }

    return undefined;
  };

  addSpecial = (index) => {
    const { data } = this.state;

    if (data[index].separateRow) {
      data[index].separateRow = null;
      data[index].expand = this.prepareExpandButton(index, false);
    } else {
      this.fetchAndRenderDetailsData(index);
      data[index].expand = this.prepareExpandButton(index, true);
    }

    this.setState({ data });
  };

  prepareExpandButton = (index, open) => (
    <ExpandButton key={index} type="button" onClick={() => this.addSpecial(index)} className={open ? 'open' : ''}>
      <Icon src={ArrowDown} alt="expand filter" />
    </ExpandButton>
  );

  convertSummaryDataFormat = (summaryData) => {
    const division = this.getDivision();
    const newData = [];
    summaryData.map((each, index) => {
      getShipmentTrackingDetailed({ division, probillNumber: each.probillNumber }).then((res) => {
        // Filter tracking items to remove statuses after "Delivered"
        const filteredTrackingItems = res.data.trackingItems
          ? this.filterDetailsAfterDelivered(res.data.trackingItems)
          : null;

        // successfull row object
        newData[index] = {
          probillNumber: each.probillNumber,
          referenceNumber: each.referenceNumber,
          deliveryDate: getDeliveryDate({ deliveryDate: each.deliveryDate, deliveryDateType: each.deliveryDateType }),
          status: each.status,
          expand: this.prepareExpandButton(index, false),
          basicData: res.data,
          detailsData: filteredTrackingItems,
          division,
        };
        // only set the state once all objects have been processed, prevents null render error
        if (Object.values(newData).length === summaryData.length) {
          this.setState({ data: newData });
        }
      }).catch(() => {
        // shipment details object where shipment summary succeeds but
        // shipment details call fails - means we can't show tracking information or view shipment page
        newData[index] = {
          probillNumber: each.probillNumber,
          referenceNumber: each.referenceNumber,
          deliveryDate: getDeliveryDate({ deliveryDate: each.deliveryDate, deliveryDateType: each.deliveryDateType }),
          status: each.status,
          expand: t('Tracking Information Not Found'),
          basicData: null,
          detailsData: null,
        };
        // only set the state once all objects have been processed
        if (Object.values(newData).length === summaryData.length) {
          this.setState({ data: newData });
        }
      });
      return undefined;
    });
  };

  onSetData = (data) => {
    switch (this.state.currentStrategy) {
      case strategies.shipment: {
        const numbers = this.getShipmentNumbers();

        const notFound = numbers.filter((num) => !data.some((e) =>
          e.referenceNumber === num
                    || e.probillNumber.trim() === num
                    || e.bolNumber === num
                    || e.poNumber === num
                    || e.customerReferenceNumber === num
                    || (e.to && e.to.postalCode === num)
                    || (e.from && e.from.postalCode === num)));

        if (notFound.length > 0) {
          this.setState({ notFound });
        }

        this.convertSummaryDataFormat(data);
        break;
      }

      default: {
        break;
      }
    }
  };

  onError = () => {
    this.setState({
      data: [],
      noResults: true,
    });
  };

  handleFormSubmit = (e) => {
    const { formData, valid } = this.props;

    if (!this.state.shipmentsFromHomePage && e) {
      e.preventDefault();
    }

    const numbers = this.getShipmentNumbers();

    if (numbers.length > MAX_TRACKING_NUMBERS) {
      this.setState({
        shipmentsFromHomePage: false,
        data: [],
        noResults: false,
        notFound: false,
      });
      return;
    }

    if (!valid && !this.state.shipmentsFromHomePage) return;

    this.setState({
      shipmentsFromHomePage: false,
      data: [],
      noResults: false,
      notFound: false,
    });

    switch (this.state.currentStrategy) {
      case strategies.shipment: {
        const { formData: { postalCode } } = this.props;

        if (formData.useOldServices) {
          apiGetShipmentSummaryByShipmentNumbers(this.onSetData, this.getDivision(), numbers, this.onError);
        } else {
          apiGetOrderTrackingData(numbers, postalCode).then(
            (res) => {
              let notFound = null;

              // Only check for not-found numbers when multiple numbers were searched
              // and the API returned fewer results than searched numbers.
              // When a single number is searched and the API returns results,
              // the API matched it internally even if the input doesn't match any visible field.
              if (numbers.length > 1 && res.data.orderItems.length < numbers.length) {
                const failedNumbers = numbers.filter((num) => !res.data.orderItems.some((e) =>
                  e.shipmentNumber === num
                                    || e.bolNumber === num
                                    || e.poNumber === num
                                    || e.customerReferenceNumber === num
                                    || (e.to && e.to.postalCode === num)
                                    || (e.from && e.from.postalCode === num)
                                    || e.probillNumber === num));
                notFound = (failedNumbers.length > 0) ? failedNumbers : null;
              }

              if (res.data.orderItems.length === 0) {
                this.setState({
                  data: [],
                  noResults: true,
                  notFound: null,
                });
              } else {
                // For each order item, fetch tracking to check for terminal status
                const terminalStatuses = ['delivered', 'livré', 'order canceled', 'commande annulée'];
                const isTerminalTouchPoint = (tp) => {
                  const st = tp.status?.toLowerCase() || '';
                  return terminalStatuses.some((s) => st.includes(s))
                    || isExceptionTouchPoint(tp.status, tp.statusCode);
                };

                const { orderItems } = res.data;
                const fetchPromises = orderItems.map((item) =>
                  apiGetOrderById(item.orderId).then((orderRes) => {
                    const tracking = orderRes.data.tracking || [];
                    let lastTerminal = null;
                    for (let i = 0; i < tracking.length; i++) {
                      const touchPoints = tracking[i].touchPoints || [];
                      for (let j = 0; j < touchPoints.length; j++) {
                        if (isTerminalTouchPoint(touchPoints[j])) {
                          lastTerminal = touchPoints[j];
                        }
                      }
                    }

                    return lastTerminal
                      ? { ...item, status: lastTerminal.status, statusCode: lastTerminal.statusCode }
                      : item;
                  }).catch(() => item),
                );

                Promise.all(fetchPromises).then((enrichedItems) => {
                  this.setState({
                    data: [{ ...res.data, orderItems: enrichedItems }],
                    noResults: false,
                    notFound,
                  });
                });
              }
            },
          ).catch(
            () => this.setState({ noResults: true }),
          );
        }

        break;
      }

      default:
      {
        break;
      }
    }
  };

  countReferences = (data) => {
    const { touch } = this.props;
    const limit = MAX_TRACKING_NUMBERS;
    let references = [];

    if (data) {
      const numbers = (data.referenceNumbers) ? data.referenceNumbers : data.shipmentNumbers;
      const source = (numbers) ? numbers.split(/\r?\n|\r|\n|,/g) : [];
      references = source.filter((item) => item.length > 0);

      if (references.length > limit) {
        touch('shipmentNumbers');
      }
    }

    return references.length;
  };

  render() {
    const {
      formData, valid, isAuthenticated, billingAccounts,
    } = this.props;

    const referenceNumbersAdded = this.countReferences(formData, valid);
    const isOverLimit = referenceNumbersAdded > MAX_TRACKING_NUMBERS;

    return (
      <div className="container">
        <Container>
          <PageTitle>{t('TrackShipments')}</PageTitle>
          <FormContainer onSubmit={this.handleFormSubmit}>
            <CheckboxContainer>
              <Field
                label={t('ShipmentsBefore')}
                component={Checkbox}
                name="useOldServices"
              />
              {(formData && formData.useOldServices)
                                && (
                                <RadioButtonsContainer>
                                  <Field
                                    name="LTLOrCommerce"
                                    component={RadioButton}
                                    options={[
                                      { label: 'LTL', value: 'LTL', defaultChecked: true },
                                      { label: 'Commerce', value: 'Commerce' },
                                    ]}
                                  />
                                </RadioButtonsContainer>
                                )}
            </CheckboxContainer>

            <NumbersInputWrapper>
              <NumbersInputInstructions>
                <NumbersInputInstructionsTitle>
                  {this.state.currentStrategy === strategies.shipment ? t('EnterShipmentNumber') : t('EnterReferenceNumber')}
                </NumbersInputInstructionsTitle>
                <NumbersInputInstructionsInfo>
                  {this.state.currentStrategy === strategies.shipment
                    ? t('TrackShipment_ShipmentInstructions')
                    : t('TrackShipment_ReferenceInstructions')}
                </NumbersInputInstructionsInfo>
              </NumbersInputInstructions>
              <InputsContainer>
                <Field
                  name="shipmentNumbers"
                  component={TextArea}
                  placeholder={t('ShipmentNumberPlaceholder')}
                  hideCounter
                  maxLength={226}
                  noMinHeight
                  isClearable
                  touched
                  additionalInfo={`${referenceNumbersAdded} / ${MAX_TRACKING_NUMBERS} ${t('Added')}`}
                  validate={[isRequired, isGroupOfShipmentNumbersValid]}
                />
                <SubmitButtonContainer>
                  <PrimaryButton type="submit" disabled={!valid || isOverLimit}>{t('Track')}</PrimaryButton>
                </SubmitButtonContainer>
              </InputsContainer>
              {
                                (formData && !formData.useOldServices)
                                && (
                                <ZipCodeInputContainer>
                                  <Field
                                    name="postalCode"
                                    component={TextInput}
                                    label={t('PostalZipCode')}
                                    optional
                                    tooltip={<Tooltip message="Tooltip" />}
                                    placeholder="L2O 2T3"
                                    validate={[isZipPostalCodeValid]}
                                  />
                                </ZipCodeInputContainer>
                                )
                            }
            </NumbersInputWrapper>

            {this.state.noResults
                            && (
                            <NoResultsDisplay>
                              <ErrorIcon />
                              <NoResultsText>{t('NoResultsFound')}</NoResultsText>
                            </NoResultsDisplay>
                            )}

            {this.state.notFound && !this.state.noResults
                            && (
                            <NoResultsDisplay>
                              <ErrorIcon />
                              <NoResultsText>{`${t('NoResultsFoundFor')} ${this.state.notFound.toString()}`}</NoResultsText>
                            </NoResultsDisplay>
                            )}

            <TableContainer>
              {this.state.data.length === 0 || (this.state.data[0].orderItems && this.state.data[0].orderItems.length) === 0
                ? null
                : (
                  <Table
                    data={this.state.data}
                    isAuthenticated={isAuthenticated}
                    useOldServices={formData.useOldServices}
                    billingAccounts={billingAccounts}
                  />
                )}
            </TableContainer>
            <QuestionsBlock />
          </FormContainer>
        </Container>
      </div>
    );
  }
}

TrackShipmentForm.propTypes = {
  formData: PropTypes.object,
  valid: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  division: PropTypes.string,
  dispatch: PropTypes.func,
  touch: PropTypes.func,
  billingAccounts: PropTypes.array,
};

TrackShipmentForm = reduxForm({
  form: formName,
  initialValues,
  validate: validateForm,
})(TrackShipmentForm);

const mstp = (state) => ({
  formData: getFormValues(formName)(state),
  isAuthenticated: state.profile.isAuthenticated,
  division: state.profile.division,
  billingAccounts: state.profile.billingAccounts,
});

export default connect(
  mstp,
)(TrackShipmentForm);
